/** Словари опций Gamma-презентаций. Отдельный модуль, а не экспорт из
 *  PresentationPills: их читает и единая панель настроек (SettingsToolsPill —
 *  сводка пилюли), а react-refresh требует, чтобы компонентные файлы
 *  экспортировали только компоненты. */

// Коды в международном формате (RU/EN/…) — компактнее полных названий и в
// чипах секции, и в сводке пилюли (просьба владельца 2026-08-13).
export const GAMMA_LANGUAGE_OPTIONS = [
	{ value: 'ru', label: 'RU' },
	{ value: 'en', label: 'EN' },
	{ value: 'de', label: 'DE' },
	{ value: 'fr', label: 'FR' },
	{ value: 'es', label: 'ES' },
	{ value: 'zh', label: 'ZH' },
]

export const GAMMA_IMAGE_SOURCE_OPTIONS = [
	{ value: 'aiGenerated', label: 'AI-генерация' },
	{ value: 'webAllImages', label: 'Из интернета' },
	{ value: 'webFreeToUse', label: 'Бесплатные фото' },
	{ value: 'pexels', label: 'Pexels' },
	{ value: 'giphy', label: 'GIF (Giphy)' },
	{ value: 'pictographic', label: 'Иконки' },
	{ value: 'themeAccent', label: 'Декор темы' },
	{ value: 'placeholder', label: 'Заглушки' },
	{ value: 'noImages', label: 'Без картинок' },
]

export const GAMMA_TEXT_AMOUNT_OPTIONS = [
	{ value: 'brief', label: 'Кратко' },
	{ value: 'medium', label: 'Средне' },
	{ value: 'detailed', label: 'Подробно' },
	{ value: 'extensive', label: 'Максимум' },
]

/** Формат готового файла: Gamma экспортирует сама, бэк кладёт как есть. */
export const GAMMA_EXPORT_OPTIONS = [
	{ value: 'pptx', label: 'PPTX' },
	{ value: 'pdf', label: 'PDF' },
	{ value: 'png', label: 'PNG' },
]

/** Кураторский список AI-моделей картинок Gamma с тирами стоимости.
 *  Тир определяет НАДБАВКУ к цене за каждый слайд (см. modelCost.ts /
 *  бэк gamma_credits_price) — worst-case тира, чтобы дорогие картинки не
 *  уводили фичу в минус. Слаги — точные enum-значения Gamma API. */
export const GAMMA_IMAGE_MODEL_OPTIONS: {
	value: string
	label: string
	tier: 'standard' | 'advanced' | 'premium' | 'ultra'
	/** Логотип провайдера/модели из public/icons — рендерится через ModelIcon
	 *  в чипах панели и рядах мобильной шторки. ideogram.svg и recraft.png —
	 *  официальные знаки-плашки (свой тёмный фон), скругляются CSS'ом. */
	iconUrl: string
}[] = [
	{ value: '', label: 'Авто (Gamma решит)', tier: 'standard', iconUrl: '/icons/gamma.svg' },
	{ value: 'flux-1-quick', label: 'Flux Быстрый', tier: 'standard', iconUrl: '/icons/flux.svg' },
	{ value: 'ideogram-v3-turbo', label: 'Ideogram Turbo', tier: 'standard', iconUrl: '/icons/ideogram.svg' },
	{ value: 'imagen-3-flash', label: 'Imagen 3 Flash', tier: 'standard', iconUrl: '/icons/imagen.svg' },
	{ value: 'flux-1-pro', label: 'Flux Pro', tier: 'advanced', iconUrl: '/icons/flux.svg' },
	{ value: 'ideogram-v3', label: 'Ideogram V3', tier: 'advanced', iconUrl: '/icons/ideogram.svg' },
	{ value: 'recraft-v3', label: 'Recraft V3', tier: 'advanced', iconUrl: '/icons/recraft.png' },
	{ value: 'dall-e-3', label: 'DALL-E 3', tier: 'premium', iconUrl: '/icons/gpt.svg' },
	{ value: 'imagen-4-pro', label: 'Imagen 4 Pro', tier: 'premium', iconUrl: '/icons/imagen.svg' },
	{ value: 'gpt-image-2', label: 'GPT Image 2', tier: 'premium', iconUrl: '/icons/gpt.svg' },
	{ value: 'flux-2-pro', label: 'Flux 2 Pro', tier: 'premium', iconUrl: '/icons/flux.svg' },
	{ value: 'imagen-4-ultra', label: 'Imagen 4 Ultra', tier: 'ultra', iconUrl: '/icons/imagen.svg' },
	{ value: 'gpt-image-2-hd', label: 'GPT Image 2 HD', tier: 'ultra', iconUrl: '/icons/gpt.svg' },
]

/** Дефолтные надбавки тиров (✦ за слайд) — зеркало бэка; админ может
 *  переопределить вариантами gamma-presentation:img_advanced/…premium/…ultra. */
export const GAMMA_IMAGE_TIER_DEFAULT_SURCHARGE: Record<string, number> = {
	standard: 0,
	advanced: 210,
	premium: 470,
	ultra: 780,
}

/** Как Gamma трактует промпт: сгенерировать по теме, сжать длинный текст
 *  или сохранить как есть (свой готовый текст на слайды). */
export const GAMMA_TEXT_MODE_OPTIONS = [
	{ value: 'generate', label: 'Сгенерировать' },
	{ value: 'condense', label: 'Сжать мой текст' },
	{ value: 'preserve', label: 'Как есть' },
]

/** Позиция элемента колонтитула (Gamma cardOptions.headerFooter):
 *  6 слотов по углам и центрам верхней/нижней кромки слайда. */
export type GammaHfPosition =
	| 'topLeft'
	| 'topCenter'
	| 'topRight'
	| 'bottomLeft'
	| 'bottomCenter'
	| 'bottomRight'

/** Содержимое слота колонтитула: свой текст, номер слайда или логотип
 *  (логотип-файл один на все logo-слоты, задаётся отдельно). */
export type GammaHfSlotContent =
	| { type: 'text'; value: string }
	| { type: 'number' }
	| { type: 'logo' }

export type GammaHfSlots = Partial<Record<GammaHfPosition, GammaHfSlotContent>>

export const GAMMA_HF_POSITION_OPTIONS: { value: GammaHfPosition; label: string }[] = [
	{ value: 'topLeft', label: 'Сверху слева' },
	{ value: 'topCenter', label: 'Сверху по центру' },
	{ value: 'topRight', label: 'Сверху справа' },
	{ value: 'bottomLeft', label: 'Снизу слева' },
	{ value: 'bottomCenter', label: 'Снизу по центру' },
	{ value: 'bottomRight', label: 'Снизу справа' },
]

/** Размер логотипа в колонтитуле (Gamma headerFooter image.size). */
export const GAMMA_HF_SIZE_OPTIONS: { value: 'sm' | 'md' | 'lg' | 'xl'; label: string }[] = [
	{ value: 'sm', label: 'S' },
	{ value: 'md', label: 'M' },
	{ value: 'lg', label: 'L' },
	{ value: 'xl', label: 'XL' },
]

export const GAMMA_TONE_OPTIONS = [
	{ value: '', label: 'Авто (Gamma решит)' },
	{ value: 'formal', label: 'Формальный' },
	{ value: 'casual', label: 'Неформальный' },
	{ value: 'persuasive', label: 'Убедительный' },
	{ value: 'friendly', label: 'Дружелюбный' },
	{ value: 'inspirational', label: 'Вдохновляющий' },
	{ value: 'educational', label: 'Обучающий' },
	{ value: 'professional', label: 'Деловой' },
]
